import { logEvent, setAnalyticsCollectionEnabled, setUserId, setUserProperties, setDefaultEventParameters } from 'firebase/analytics';
import { analyticsLocation } from '../utils/analyticsUrl';
import { analytics } from '../firebase/config';

/**
 * Analytics Service
 * Centralized service for tracking user interactions and events with Google Analytics
 */

class AnalyticsService {
  private isEnabled: boolean = false;
  private pendingCollectionSyncTimeout: ReturnType<typeof setTimeout> | null = null;

  /**
   * Check if analytics is available and enabled
   */
  private isAvailable(): boolean {
    return this.isEnabled && analytics !== null;
  }

  /**
   * Enable analytics tracking
   */
  enable() {
    this.setConsent(true);
  }

  /**
   * Disable analytics tracking (for privacy/GDPR compliance)
   */
  disable() {
    this.setConsent(false);
  }

  /**
   * Apply consent-driven analytics collection state.
   */
  setConsent(enabled: boolean) {
    this.isEnabled = enabled;
    this.syncCollectionState();
    console.log(`📊 Analytics ${enabled ? 'enabled' : 'disabled'} by cookie consent`);
  }

  private syncCollectionState(retryCount: number = 0) {
    if (analytics) {
      try {
        setAnalyticsCollectionEnabled(analytics, this.isEnabled);
      } catch (error) {
        console.warn('Analytics collection toggle failed:', error);
      }
      return;
    }

    // Firebase Analytics initializes asynchronously, so retry briefly.
    if (retryCount < 5) {
      if (this.pendingCollectionSyncTimeout) {
        clearTimeout(this.pendingCollectionSyncTimeout);
      }
      this.pendingCollectionSyncTimeout = setTimeout(() => {
        this.syncCollectionState(retryCount + 1);
      }, 500);
    }
  }

  /**
   * Set the user ID for analytics
   */
  setUser(userId: string | null) {
    if (!this.isAvailable() || !analytics) return;
    
    try {
      if (userId) {
        setUserId(analytics, userId);
        console.log('📊 Analytics user ID set');
      } else {
        setUserId(analytics, null);
        console.log('📊 Analytics user ID cleared');
      }
    } catch (error) {
      console.error('Analytics setUser error:', error);
    }
  }

  /**
   * Set user properties for analytics
   */
  setUserProperties(properties: Record<string, any>) {
    if (!this.isAvailable() || !analytics) return;
    
    try {
      setUserProperties(analytics, properties);
      console.log('📊 Analytics user properties set:', properties);
    } catch (error) {
      console.error('Analytics setUserProperties error:', error);
    }
  }

  /**
   * Track page views
   */
  trackPageView(pagePath: string, pageTitle?: string) {
    if (!this.isAvailable() || !analytics) return;
    
    try {
      const page_location = analyticsLocation();
      // Later events in this page inherit the cleaned location too.
      setDefaultEventParameters({ page_location, page_path: pagePath });
      logEvent(analytics, 'page_view', {
        page_path: pagePath,
        page_title: pageTitle || document.title,
        page_location,
      });
      // console.log('📊 Page view tracked:', pagePath);
    } catch (error) {
      console.error('Analytics trackPageView error:', error);
    }
  }

  /**
   * Track authentication events
   */
  trackAuth(action: 'login' | 'signup' | 'logout', method?: string) {
    if (!this.isAvailable() || !analytics) return;
    
    try {
      // Firebase Analytics uses 'sign_up' instead of 'signup'
      const eventName = action === 'signup' ? 'sign_up' : action;
      
      logEvent(analytics, eventName as any, {
        method: method || 'email',
      });
      console.log(`📊 Auth tracked: ${action}`);
    } catch (error) {
      console.error('Analytics trackAuth error:', error);
    }
  }

  /**
   * Track profile completion
   */
  trackProfileComplete(userId: string) {
    if (!this.isAvailable() || !analytics) return;
    
    try {
      logEvent(analytics, 'complete_profile', {
        user_id: userId,
      });
      console.log('📊 Profile completion tracked');
    } catch (error) {
      console.error('Analytics trackProfileComplete error:', error);
    }
  }

  /**
   * Track onboarding steps
   */
  trackOnboarding(step: string, completed: boolean = false) {
    if (!this.isAvailable() || !analytics) return;
    
    try {
      logEvent(analytics, 'onboarding_step', {
        step_name: step,
        completed,
      });
      console.log(`📊 Onboarding tracked: ${step}`);
    } catch (error) {
      console.error('Analytics trackOnboarding error:', error);
    }
  }

  /**
   * Track campaign clicks
   */
  trackCampaign(campaignCode: string) {
    if (!this.isAvailable() || !analytics) return;
    
    try {
      logEvent(analytics, 'campaign_click', {
        campaign_code: campaignCode,
        timestamp: new Date().toISOString(),
      });
      console.log(`📊 Campaign tracked: ${campaignCode}`);
    } catch (error) {
      console.error('Analytics trackCampaign error:', error);
    }
  }

  /**
   * Track subscription purchase events
   * Fires to both Firebase Analytics and Meta Pixel
   */
  trackPurchase(params: {
    productName: string;
    priceId?: string;
    value?: number;
    currency?: string;
    billingPeriod?: 'quarter' | 'year';
    userId?: string;
  }) {
    const { productName, priceId, value, currency = 'USD', billingPeriod, userId } = params;

    // Firebase Analytics purchase event
    if (this.isAvailable() && analytics) {
      try {
        logEvent(analytics, 'purchase', {
          currency,
          value: value || 0,
          items: [
            {
              item_id: priceId || 'premium',
              item_name: productName,
              item_category: 'subscription',
              price: value || 0,
              quantity: 1,
            },
          ],
          transaction_id: `sub_${Date.now()}`,
        });
        console.log(`📊 Firebase purchase tracked: ${productName}`);
      } catch (error) {
        console.error('Analytics trackPurchase (Firebase) error:', error);
      }
    }

    // Meta Pixel Purchase event
    try {
      if (typeof window !== 'undefined' && (window as any).fbq) {
        (window as any).fbq('track', 'Purchase', {
          content_name: productName,
          content_type: 'subscription',
          content_ids: [priceId || 'premium'],
          value: value || 0,
          currency,
          billing_period: billingPeriod,
        });
        console.log(`📊 Meta Pixel purchase tracked: ${productName}`);
      }
    } catch (error) {
      console.error('Analytics trackPurchase (Meta Pixel) error:', error);
    }

    // Also fire a custom event for more detailed tracking
    if (this.isAvailable() && analytics) {
      try {
        logEvent(analytics, 'subscription_purchased', {
          product_name: productName,
          price_id: priceId,
          billing_period: billingPeriod,
          user_id: userId,
          timestamp: new Date().toISOString(),
        });
        console.log(`📊 Custom subscription event tracked`);
      } catch (error) {
        console.error('Analytics subscription_purchased error:', error);
      }
    }
  }
}

// Export a singleton instance
export const analyticsService = new AnalyticsService();

